import { Form, Formik, FormikHelpers } from "formik";
import { LoaderButton } from "../../components/common/LoadingButton";
import FormInput from "../../components/common/FormInput";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  resetPasswordInitialValues,
  resetPasswordSchema,
} from "utils/validation";
import { useAuth } from "hooks/useAuth";
import { useEffect } from "react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { resetPassword, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const resetKey = searchParams.get("reset-token") ?? "";
  // guard - redirect if no key
  useEffect(() => {
    if (!resetKey) navigate("/forgot-password", { replace: true });
  }, [resetKey, navigate]);

  // in handleSubmit:
  const handleSubmit = async (
    values: { password: string; confirmPassword: string },
    actions: FormikHelpers<{ password: string; confirmPassword: string }>,
  ) => {
    await resetPassword(resetKey, values.password);
    actions.resetForm();
  };

  return (
    <div className="bg-primary  rounded-2xl border shadow-lg border-[#222735] w-full py-8 px-2 md:px-4">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-[#1E2230] border border-[#6366F1]/40 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-[#6366F1]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        <div className="text-center mb-2">
          <h1 className="font-semibold text-xl md:text-2xl w-full mt-[18px]">
            Reset Password
          </h1>
          <p className="text-sm text-neutral-medium pt-[7px]">
            Please enter your new password below
          </p>
        </div>

        <Formik
          initialValues={resetPasswordInitialValues}
          validationSchema={resetPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            dirty,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="px-4 md:px-16 pt-6 text-left">
                <FormInput
                  name="password"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  placeholder="Enter new password"
                  darkBg
                  whiteLabel
                />
                <FormInput
                  name="confirmPassword"
                  type="password"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && errors.confirmPassword}
                  placeholder="Confirm new password"
                  darkBg
                  whiteLabel
                />
                <LoaderButton
                  disabled={!isValid || !dirty || isSubmitting}
                  className="!w-full"
                  text="Change Password"
                  isLoading={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>

        <p className="text-center text-sm text-gray-400 mt-6">
          Know your password?{" "}
          <Link to="/login" className="text-[#6366F1] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
